import { auth, db } from '../firebase';

const SIGNUP_BONUS_POINTS = 200;
const DAILY_POINTS_AUTH = 20;
const DAILY_POINTS_ANON = 10;

/**
 * Check if two timestamps are the same day (UTC).
 */
const isSameDay = (ts1, ts2) => {
    const d1 = new Date(ts1);
    const d2 = new Date(ts2);
    return (
        d1.getUTCFullYear() === d2.getUTCFullYear() &&
        d1.getUTCMonth() === d2.getUTCMonth() &&
        d1.getUTCDate() === d2.getUTCDate()
    );
};

/**
 * Get or create a unique voter ID.
 * If user is authenticated, returns their Firebase uid.
 * Otherwise, returns/creates an anonymous localStorage ID.
 * Initializes new anonymous users in the DB with 0 points.
 */
export const getOrCreateVoterId = () => {
    if (typeof window === 'undefined') return null;

    const currentUser = auth.currentUser;
    if (currentUser) {
        localStorage.setItem('voterId', currentUser.uid);
        return currentUser.uid;
    }

    let voterId = localStorage.getItem('voterId');
    if (!voterId) {
        voterId = 'voter_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
        localStorage.setItem('voterId', voterId);
        // Initialize new anon user in DB with 0 points
        if (db) {
            db.ref(`users/${voterId}`).set({
                points: 0,
                lastRefill: 0,
                totalVotes: 0,
                lastActive: Date.now(),
                isAuthenticated: false
            });
        }
    }
    return voterId;
};


export const claimDailyPoints = async () => {
    const voterId = getOrCreateVoterId();
    if (!voterId || !db) return { success: false, points: 0 };

    const currentUser = auth.currentUser;
    const isAuth = !!currentUser;
    const now = Date.now();

    let result = { success: false, points: 0, alreadyClaimed: false };

    await db.ref(`users/${voterId}`).transaction((currentData) => {
        if (!currentData) {
            // Initialize if somehow missing
            currentData = { points: 0, lastRefill: 0, totalVotes: 0, isAuthenticated: isAuth };
        }

        const lastRefill = currentData.lastRefill || 0;
        if (lastRefill && isSameDay(lastRefill, now)) {
            result = { success: false, points: currentData.points || 0, alreadyClaimed: true };
            return currentData; // Abort, already claimed
        }

        let newPoints;
        if (isAuth) {
            newPoints = (currentData.points || 0) + DAILY_POINTS_AUTH;
        } else {
            newPoints = DAILY_POINTS_ANON; // Reset to 10 for anon
        }

        result = { success: true, points: newPoints, alreadyClaimed: false };

        return {
            ...currentData,
            points: newPoints,
            lastRefill: now,
            lastActive: now,
            isAuthenticated: isAuth
        };
    });

    return result;
};


export const linkAnonymousVotes = async (user) => {
    if (!db || !user) return;

    const oldVoterId = localStorage.getItem('voterId');
    const newUid = user.uid;

    // Check if this user has already been set up (returning auth user)
    const authSnapshot = await db.ref(`users/${newUid}`).once('value');
    const authData = authSnapshot.val();

    // If already an authenticated account (returning user)
    if (authData && authData.isAuthenticated) {
        localStorage.setItem('voterId', newUid);
        await db.ref(`users/${newUid}`).update({
            displayName: user.displayName,
            photoURL: user.photoURL,
            lastActive: Date.now()
        });
        return;
    }

    // New signup or first migration
    let anonData = null;
    if (oldVoterId && oldVoterId.startsWith('voter_') && oldVoterId !== newUid) {
        const anonSnapshot = await db.ref(`users/${oldVoterId}`).once('value');
        anonData = anonSnapshot.val();
    }

    const mergedVotes = (authData?.totalVotes || 0) + (anonData?.totalVotes || 0);
    // Anon points are NOT carried over, they get the signup bonus instead
    const newPoints = (authData?.points || 0) + SIGNUP_BONUS_POINTS;

    await db.ref(`users/${newUid}`).set({
        displayName: user.displayName,
        photoURL: user.photoURL,
        isAuthenticated: true,
        totalVotes: mergedVotes,
        points: newPoints,
        lastRefill: authData?.lastRefill || 0,
        lastActive: Date.now(),
        linkedFrom: (anonData && oldVoterId) ? oldVoterId : null,
        signupBonusAwarded: true
    });

    if (anonData && oldVoterId) {
        await db.ref(`users/${oldVoterId}`).remove();
    }

    localStorage.setItem('voterId', newUid);
};

/**
 * Reset voterId on sign-out to prevent vote migration between accounts.
 */
export const resetVoterIdOnSignOut = () => {
    const currentVoterId = localStorage.getItem('voterId');
    if (currentVoterId && !currentVoterId.startsWith('voter_')) {
        localStorage.removeItem('voterId');
        const newVoterId = 'voter_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
        localStorage.setItem('voterId', newVoterId);
        // Initialize new anon user in DB
        if (db) {
            db.ref(`users/${newVoterId}`).set({
                points: 0,
                lastRefill: 0,
                totalVotes: 0,
                lastActive: Date.now(),
                isAuthenticated: false
            });
        }
    }
};
